import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CreditCard, Plus, Printer, RotateCcw, Search, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { RecordPaymentForm, PAYMENT_METHODS } from "../components/RecordPaymentForm";
import { Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from "../components/ui";
import { formatDate, formatMoney } from "../lib/format";
import {
  PAYMENT_METHOD_LABELS,
  todayInAccountZone,
  validateRefund,
} from "../lib/payment";
import { isAccountAdmin, isManagerOrAbove } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";

/**
 * The payments register.
 *
 * One row per recorded receipt, newest first. Money in and money out are the
 * same table with a stated direction, so a refund never hides among the
 * receipts. Recording is manager work; refunding is owner/admin (BR-33).
 */
export function PaymentsPage() {
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const search = params.get("search") ?? "";
  const direction = params.get("direction") ?? "";
  const method = params.get("method") ?? "";
  const cursor = params.get("cursor") ?? "";

  const [draft, setDraft] = useState(search);
  const debounceTimer = useRef<number | undefined>(undefined);
  const [showRecord, setShowRecord] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  useEffect(() => setDraft(search), [search]);
  useEffect(() => () => window.clearTimeout(debounceTimer.current), []);

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  function onDraftChange(value: string) {
    setDraft(value);
    window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(
      () => update({ search: value, cursor: "" }),
      250,
    );
  }

  const query = useQuery({
    queryKey: ["payments", search, direction, method, cursor],
    queryFn: ({ signal }) =>
      api.payments(
        {
          search: search || undefined,
          direction: direction || undefined,
          method: method || undefined,
          cursor: cursor || undefined,
          page_size: 25,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const payments = query.data?.results ?? [];
  const nextCursor = cursorFrom(query.data?.next);
  const canRecord = isManagerOrAbove(user?.role);
  const canRefund = isAccountAdmin(user?.role);
  const filtering = Boolean(search || direction || method);

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle="Every receipt and refund, with the direction stated explicitly."
        actions={
          canRecord ? (
            <Button variant="primary" onClick={() => setShowRecord((v) => !v)}>
              <Plus size={14} /> Record payment
            </Button>
          ) : undefined
        }
      />

      {showRecord ? (
        <RecordPaymentForm
          onDone={() => {
            setShowRecord(false);
            void queryClient.invalidateQueries({ queryKey: ["payments"] });
            void queryClient.invalidateQueries({ queryKey: ["billing"] });
          }}
          onCancel={() => setShowRecord(false)}
        />
      ) : null}

      {showRefund ? (
        <RefundForm
          onDone={() => {
            setShowRefund(false);
            void queryClient.invalidateQueries({ queryKey: ["payments"] });
            void queryClient.invalidateQueries({ queryKey: ["billing"] });
          }}
          onCancel={() => setShowRefund(false)}
        />
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Search receipts or references"
            aria-label="Search payments"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        <div className="flex gap-1">
          {["", "inbound", "outbound"].map((value) => (
            <button
              key={value || "all"}
              onClick={() => update({ direction: value, cursor: "" })}
              className={
                direction === value
                  ? "rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white"
                  : "rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              }
            >
              {value === "" ? "All" : value === "inbound" ? "Received" : "Refunded"}
            </button>
          ))}
        </div>

        {canRefund ? (
          <Button variant="secondary" onClick={() => setShowRefund((v) => !v)}>
            <RotateCcw size={14} /> Refund
          </Button>
        ) : null}

        <select
          value={method}
          onChange={(event) => update({ method: event.target.value, cursor: "" })}
          aria-label="Filter by payment method"
          className="rounded-md border-0 bg-white py-2 pl-3 pr-8 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        >
          <option value="">All methods</option>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        {filtering ? (
          <Button variant="ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <X size={14} /> Clear
          </Button>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={8} />
        ) : query.isError ? (
          <EmptyState
            title="Could not load payments"
            body={query.error instanceof ApiError ? query.error.message : "Please try again."}
          />
        ) : payments.length === 0 ? (
          <EmptyState
            icon={<CreditCard size={28} />}
            title={filtering ? "Nothing matches that" : "No payments yet"}
            body={
              filtering
                ? "Try a different reference, or clear the filters."
                : "Receipts you record will appear here."
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Receipt</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Method</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium tabular-nums text-slate-900">
                    {payment.receipt_number || "—"}
                    {payment.reference ? (
                      <span className="ml-2 text-xs text-slate-500">{payment.reference}</span>
                    ) : null}
                    {payment.invoice_number ? (
                      <span className="ml-2 text-xs text-slate-400">{payment.invoice_number}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    {payment.contact_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    {formatDate(payment.payment_date, timeZone)}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize text-slate-600">{payment.method}</td>
                  <td className="px-4 py-3">
                    <Badge tone={payment.direction === "outbound" ? "rose" : "emerald"}>
                      {payment.direction === "outbound" ? "Refunded" : "Received"}
                    </Badge>
                    {payment.is_advance ? (
                      <span className="ml-2 text-xs text-slate-400">advance</span>
                    ) : null}
                  </td>
                  <td
                    className={
                      payment.direction === "outbound"
                        ? "px-4 py-3 text-right text-sm font-medium tabular-nums text-rose-700"
                        : "px-4 py-3 text-right text-sm font-medium tabular-nums text-emerald-700"
                    }
                  >
                    {payment.direction === "outbound" ? "−" : "+"}
                    {formatMoney(payment.amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/billing/payments/${payment.id}/receipt`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                    >
                      <Printer size={13} /> Receipt
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <nav className="mt-4 flex items-center justify-between">
        <Button onClick={() => update({ cursor: "" })} disabled={!cursor || query.isFetching}>
          <ChevronLeft size={16} /> First
        </Button>
        <span className="text-xs text-slate-500">
          {query.isFetching ? "Loading." : cursor ? "Next page" : "First page"}
        </span>
        <Button
          onClick={() => nextCursor && update({ cursor: nextCursor })}
          disabled={!nextCursor || query.isFetching}
        >
          Next <ChevronRight size={16} />
        </Button>
      </nav>
    </>
  );
}

const PAYABLE = new Set(["sent", "part_paid", "overdue"]);

function RefundForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const timeZone = useAccountTimeZone();
  const invoices = usePayableInvoices();
  const [invoice, setInvoice] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("netbanking");
  const [reference, setReference] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => todayInAccountZone(timeZone));
  const [notes, setNotes] = useState("");

  const selected = invoices.data?.results.find((row) => row.id === invoice);
  const refundable = Number(selected?.amount_paid?.amount ?? 0);
  const errors = validateRefund({
    amount,
    paymentDate,
    method,
    reference,
    outstanding: 0,
    refundable,
  });
  const valid = Boolean(invoice) && Object.keys(errors).length === 0;

  const save = useMutation({
    mutationFn: () =>
      api.refundPayment({
        invoice,
        amount: amount.trim(),
        method,
        reference: reference.trim(),
        notes: notes.trim(),
        payment_date: paymentDate || undefined,
      }),
    onSuccess: onDone,
  });

  return (
    <Card className="mb-4">
      <h2 className="mb-1 text-sm font-semibold">Record a refund</h2>
      <p className="mb-4 text-sm text-slate-500">
        Money going back to the customer, recorded as an outbound payment.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) save.mutate();
        }}
        className="grid gap-4 sm:grid-cols-2"
      >
        <Field label="Invoice" className="sm:col-span-2">
          <select value={invoice} onChange={(e) => setInvoice(e.target.value)} className={INPUT}>
            <option value="">Select an invoice…</option>
            {invoices.data?.results.map((row) => (
              <option key={row.id} value={row.id}>
                {row.number || "Draft"} · {row.contact_name || "—"} ·{" "}
                {formatMoney(row.amount_paid)} paid
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount">
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className={INPUT}
          />
          {errors.amount ? <FieldError>{errors.amount}</FieldError> : null}
        </Field>
        <Field label="Refund date">
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className={INPUT}
          />
          {errors.payment_date ? <FieldError>{errors.payment_date}</FieldError> : null}
        </Field>
        <Field label="Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={method === "cash" ? "Optional" : "UPI / card / transaction id"}
            className={INPUT}
          />
          {errors.reference ? <FieldError>{errors.reference}</FieldError> : null}
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
        </Field>

        {save.isError ? (
          <p role="alert" className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {save.error instanceof ApiError ? save.error.message : "Could not record the refund."}
          </p>
        ) : null}

        <div className="flex items-center gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending || !valid}>
            {save.isPending ? "Recording…" : "Record refund"}
          </Button>
          <Button onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mt-1 text-xs text-rose-600">
      {children}
    </p>
  );
}

/** Invoices a payment can be recorded against: issued, not void. */
function usePayableInvoices() {
  return useQuery({
    queryKey: ["invoices", "payable"],
    queryFn: ({ signal }) => api.invoices({ page_size: 200 }, signal),
    select: (page) => ({
      ...page,
      results: page.results.filter((row) => PAYABLE.has(row.status)),
    }),
    staleTime: 30_000,
  });
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

/** `next` is an absolute URL; the cursor is its trailing query parameter. */
function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}
